import { z } from 'zod';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { CirclePlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Input from '@/components/form/Input';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useTenants, usePostTenant } from '@/data/tenant';

const formSchema = z.object({
  name: z.string().min(1),
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  email: z.string().email()
});

const AddTenantDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postTenant = usePostTenant();
  const tenants = useTenants();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      firstname: '',
      lastname: '',
      email: ''
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      const tenant = await postTenant(values);
      await tenants.mutate(
        (currentTenants) => [...currentTenants, tenant],
        false
      );
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          Add Tenant
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Tenant</DialogTitle>
          <DialogDescription>
            Add a Tenant such as &quot;Big White&quot; and provide admin
            details.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input name="name" label="Tenant Name" autoComplete="off" />
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input
                  name="firstname"
                  label="Admin Owner First Name"
                  autoComplete="given-name"
                />
              </div>
              <div className="flex-1">
                <Input
                  name="lastname"
                  label="Last Name"
                  autoComplete="family-name"
                />
              </div>
            </div>
            <Input
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Add Tenant</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTenantDialog;
